import React, { useEffect, useRef, useState } from "react";

/* Show/hide fixed-table columns — same UX pattern as the Waterfall/EAC report's own column
   picker (src/pages/powerbi/reports/waterfall-eac/ThreeYearView.js: ColsIcon + "N Hidden"
   badge + a checkbox dropdown + "Show All Columns" reset), adapted to this screen's own
   accent color (#0070D2, matching the Download/Upload/Attachment icons already used here)
   instead of that report's indigo theme. Generic over any {key, label} column list so both
   RenderLabor.js and RenderLaborODC.js can reuse it. `hidden` is a plain array of hidden keys
   (not a Set) so the parent can keep it in ordinary React state without extra Set-copying
   boilerplate at each call site. */
function ColsIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}

export default function ColumnPicker({ columns, hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const hiddenCount = hidden.length;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", flexShrink: 0, marginBottom: "4px" }}
    >
      <button
        type="button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          height: 30,
          padding: "0 12px",
          borderRadius: 15,
          border: `1px solid ${hiddenCount > 0 ? "#B6C7EE" : "#898b8c"}`,
          background: hiddenCount > 0 ? "#EAF1FC" : "#fff",
          color: hiddenCount > 0 ? "#0070D2" : "#374151",
          fontSize: 12.5,
          fontWeight: 500,
          cursor: "pointer",
        }}
        onClick={() => setOpen((p) => !p)}
      >
        <ColsIcon />
        Columns
        {hiddenCount > 0 && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#0070D2",
              background: "rgba(0,112,210,0.12)",
              borderRadius: 4,
              padding: "1px 6px",
              marginLeft: 2,
            }}
          >
            {hiddenCount} hidden
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1200,
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            padding: "6px 0 8px",
            minWidth: 220,
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {columns.map((col) => (
            <label
              key={col.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 16px",
                cursor: "pointer",
                fontSize: 12.5,
                color: "#374151",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={!hidden.includes(col.key)}
                onChange={() => onToggle(col.key)}
                style={{
                  width: 14,
                  height: 14,
                  accentColor: "#0070D2",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              {col.label}
            </label>
          ))}

          {hiddenCount > 0 && (
            <button
              type="button"
              style={{
                margin: "6px 12px 0",
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid #E2E8F0",
                background: "transparent",
                color: "#6B7280",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                textAlign: "center",
              }}
              onClick={onReset}
            >
              Show All Columns
            </button>
          )}
        </div>
      )}
    </div>
  );
}
